// ============================================================================
// MEAL PLAN SERVICE
// ============================================================================
// Business logic and API calls for Meal Plan module

import { get, post, put, del } from '../client/axiosClient';
import { mealPlanEndpoints } from '../endpoints/mealPlan.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  MealPlan,
  CreateMealPlanRequest,
  UpdateMealPlanRequest,
  MealPlanSearchRequest,
} from '../types';

/**
 * Meal Plan Service Class
 */
class MealPlanService {
  /**
   * Get all meal plans (paginated)
   */
  async getAll(params?: MealPlanSearchRequest): Promise<PaginatedResponse<MealPlan>> {
    const endpoint = params
      ? buildEndpointWithQuery('/MealPlan', params)
      : mealPlanEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw.data ?? {};

    return {
      success: raw.success,
      message: raw.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  /**
   * Get meal plan by ID
   */
  async getById(id: ID): Promise<ApiResponse<MealPlan>> {
    const response = await get<ApiResponse<MealPlan>>(mealPlanEndpoints.getById(id));
    return response.data;
  }

  /**
   * Get meal plan by code
   */
  async getByCode(code: string, propertyId?: string): Promise<ApiResponse<MealPlan>> {
    let endpoint = mealPlanEndpoints.getByCode(code);
    if (propertyId) {
      endpoint = `${endpoint}?propertyId=${propertyId}`;
    }
    const response = await get<ApiResponse<MealPlan>>(endpoint);
    return response.data;
  }

  /**
   * Create new meal plan
   */
  async create(data: CreateMealPlanRequest): Promise<ApiResponse<MealPlan>> {
    const response = await post<ApiResponse<MealPlan>>(
      mealPlanEndpoints.create(),
      data
    );
    return response.data;
  }

  /**
   * Update meal plan
   */
  async update(id: ID, data: UpdateMealPlanRequest): Promise<ApiResponse<MealPlan>> {
    const response = await put<ApiResponse<MealPlan>>(
      mealPlanEndpoints.update(id),
      data
    );
    return response.data;
  }

  /**
   * Delete meal plan
   */
  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(mealPlanEndpoints.delete(id), {
      params: { rowVersion },
    });
    return response.data;
  }

  /**
   * Validate meal plan code uniqueness
   */
  async validateCode(code: string, propertyId?: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    let endpoint = mealPlanEndpoints.validateCode(code);
    const queryParts: string[] = [];
    if (propertyId) queryParts.push(`propertyId=${propertyId}`);
    if (excludeId) queryParts.push(`excludeId=${excludeId}`);
    if (queryParts.length > 0) {
      endpoint = `${endpoint}?${queryParts.join('&')}`;
    }
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }

  async validateName(name: string, propertyId?: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    let endpoint = mealPlanEndpoints.validateName(encodeURIComponent(name));
    const queryParts: string[] = [];
    if (propertyId) queryParts.push(`propertyId=${propertyId}`);
    if (excludeId) queryParts.push(`excludeId=${excludeId}`);
    if (queryParts.length > 0) {
      endpoint = `${endpoint}?${queryParts.join('&')}`;
    }
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const mealPlanService = new MealPlanService();
export default mealPlanService;
